import React, { useContext, useState, useEffect, useRef } from "react";
import { Modal } from "reactstrap";
import { Language, LoginUserData } from "../../../App";
import { RiImageAddLine, RiCloseLine } from "react-icons/ri";
import { 
  MdOutlineSchool, 
  MdWorkspacePremium, 
  MdAttachMoney, 
  MdOutlineDescription,
  MdFormatListBulleted,
  MdLightbulbOutline
} from "react-icons/md";
import { IoClose } from "react-icons/io5";
import ImageCropModal from "../../common/ImageCropModal";
import CertificateModal from "./CertificateModal";
import axios from "axios";
import { toast } from "react-toastify";
import { adminBaseURL } from "../../../constants/API";
import Select from "react-select";
import { useCheckSubscriptionPermission } from "../../../hooks/useCheckSubscriptionPermission";
import "../../feeds/postTabs/formTheme.css";

const CLOSE_ANIMATION_MS = 320;

const CreateCourseModal = ({ isOpen, toggle }) => {
  const { lang } = useContext(Language);
  const { splashData } = useContext(LoginUserData);
  const checkPermission = useCheckSubscriptionPermission('upload_course');
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(null);
  const [isCertificateModelOpen, setIsCertificateModelOpen] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  // Smooth slide-in/out state matching CreateMentorship.jsx pattern
  const [slideIn, setSlideIn] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(() => window.innerWidth <= 768);
  const closeTimeoutRef = useRef(null);

  // Responsive screen listener
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Animate in on mount with double-rAF like CreateMentorship.jsx
  useEffect(() => {
    if (isOpen) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      let raf2;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setSlideIn(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    }
    setSlideIn(false);
  }, [isOpen]);

  // Clean up pending close timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  // Lock body & html scroll when open
  useEffect(() => {
    if (!isOpen) return undefined;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Close with animation: animate out first, then toggle() once transition finishes
  const requestClose = () => {
    if (!isOpen || closeTimeoutRef.current) return;
    setSlideIn(false);
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      toggle();
    }, CLOSE_ANIMATION_MS);
  };

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === "Escape") requestClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // State for course data
  const [data, setData] = useState({
    skills: "",
    short_description: "",
    description: "",
    instruction: "",
    about_1: "",
    about_2: "",
    about_3: "",
    about_4: "",
    price: "",
    isFree: false,
    subcategory: "",
    title: "",
    language: [],
  });

  const [errors, setErrors] = useState({
    skill: "",
    description: "",
    instruction: "",
    about_1: "",
    about_2: "",
    about_3: "",
    about_4: "",
    price: "",
    subcategory: "",
    title: "",
    cover: "",
    language: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setImageSrc(reader.result);
        setIsCropModalOpen(true);
      };
    }
    event.target.value = null;
  };

  const handleCropComplete = (croppedImageURL) => {
    setCroppedImage(croppedImageURL);
    setIsCropModalOpen(false);
  };

  const handleRemoveImage = () => {
    setCroppedImage(null);
    setImageSrc(null);
  };

  const toggleImagePreview = (imagePreview) => setIsImagePreviewOpen(imagePreview);

  const handleAddCertificate = (index, certificate) => {
    if (index !== null) {
      setCertificates((prev) => {
        const updatedCertificates = [...prev];
        updatedCertificates[index] = certificate;
        return updatedCertificates;
      });
    } else {
      setCertificates((prev) => [...prev, certificate]);
    }
  };

  const handleOpenModalCertificateModal = (certificate, index) => {
    setSelectedCertificate({ ...certificate, index });
    setIsCertificateModelOpen(true);
  };

  const handleCloseModalCertificateModal = () => {
    setSelectedCertificate(null);
    setIsCertificateModelOpen(false);
  };

  const handleRemoveCertificate = (index) => {
    const newCertificates = certificates.filter((_, i) => i !== index);
    setCertificates(newCertificates);
  };

  const validateForm = () => {
    let formValid = true;

    const newErrors = {
      instruction: "",
      price: "",
      subcategory: "",
      title: "",
      cover: "",
      language: "",
    };

    let errorMessage = "";

    if (!croppedImage) {
      errorMessage = lang.course_cover_image_is_required;
      formValid = false;
    } else if (!data.title) {
      errorMessage = lang.course_title_is_required;
      formValid = false;
    } else if (data.title.length > 50) {
      errorMessage = lang.course_title_too_long || "Title must not exceed 50 characters";
      formValid = false;
    } else if (!data.subcategory) {
      errorMessage = lang.course_category_is_required;
      formValid = false;
    } else if (!data.description) {
      errorMessage = lang.course_description_is_required;
      formValid = false;
    } else if (data.language.length === 0) {
      errorMessage = lang.course_language_is_required || "Please select at least one language";
      formValid = false;
      newErrors.language = errorMessage;
    }

    if (!formValid && errorMessage) {
      toast.error(errorMessage);
    }

    setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
    return formValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!checkPermission()) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    if (!data.subcategory || data.subcategory === "") {
      toast.error(lang?.select_course_subcategory_please);
      return;
    }

    if (!data.isFree && (!data.price || data.price <= 0)) {
      toast.error("Course Price must be above zero");
      return;
    }

    const certificateTitles = certificates.map(
      (certificate) => certificate.title
    );
    const commaSeparatedTitles = certificateTitles.join(", ");

    const certificateImages = certificates.map(
      (certificate) => certificate.file
    );

    const commaSeparatedLanguages = data.language.join(", ");

    setIsLoading(true);

    try {
      const token = JSON.parse(localStorage.getItem("SignupData"))?.data?.token;
      const res = await axios.post(
        `${adminBaseURL}user/course/create`,
        {
          title: data.title,
          short_description: data.short_description,
          price: data.isFree ? 0 : data.price,
          category: "3",
          subcategory: data.subcategory,
          about_1: data.about_1,
          about_2: data.about_2,
          about_3: data.about_3,
          about_4: data.about_4,
          skills: data.skills,
          description: data.description,
          cover_base64: croppedImage,
          titles: commaSeparatedTitles,
          images: certificateImages,
          language: commaSeparatedLanguages,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.status) {
        setIsLoading(false);
        toast.success(lang.course_saved_successfully);
        requestClose();
        setTimeout(() => {
          window.location.reload();
        }, CLOSE_ANIMATION_MS + 50);
      } else {
        setIsLoading(false);
        toast.error(res.data.action);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      setIsLoading(false);
    }
  };

  const languageOptions = [
    { value: "English", label: "English" },
    { value: "Spanish", label: "Spanish" },
  ];

  const handleLanguageChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setData({ ...data, language: selectedValues });
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "40px",
      borderRadius: "10px",
      border: state.isFocused ? "1px solid #00BADA" : "1px solid #E2E8F0",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(0, 186, 218, 0.28)" : "none",
      backgroundColor: "#FFFFFF",
      fontFamily: "'Outfit', 'Onest', sans-serif",
      fontSize: "13px",
      transition: "all 0.18s ease",
      "&:hover": {
        borderColor: "#CBD5E1",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 10px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#E0F7FC",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#0096B3",
      fontWeight: "600",
      fontSize: "12px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#0096B3",
      "&:hover": {
        backgroundColor: "#BCEEFA",
        color: "#0B132B",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#94A3B8",
      fontSize: "13px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: "12px",
      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.08)",
      border: "1px solid #E2E8F0",
      overflow: "hidden",
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "13px",
      backgroundColor: state.isSelected ? "#00BADA" : state.isFocused ? "#E0F7FC" : "transparent",
      color: state.isSelected ? "#FFFFFF" : "#0B132B",
      cursor: "pointer",
    }),
  };

  return (
    <>
      {/* Backdrop with fade transition */}
      <div
        onClick={requestClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(15, 23, 42, 0.45)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          zIndex: 1050,
          opacity: slideIn ? 1 : 0,
          transition: `opacity ${CLOSE_ANIMATION_MS}ms ease-in-out`,
        }}
      />

      {/* Slide-in panel matching CreateMentorship.jsx pattern */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: isSmallScreen ? "100%" : "560px",
          maxWidth: "100%",
          background: "#FFFFFF",
          zIndex: 1051,
          display: "flex",
          flexDirection: "column",
          borderTopLeftRadius: isSmallScreen ? 0 : "20px",
          borderBottomLeftRadius: isSmallScreen ? 0 : "20px",
          boxShadow: "-12px 0 40px rgba(15, 23, 42, 0.16)",
          overflow: "hidden",
          transform: slideIn ? "translateX(0)" : "translateX(100%)",
          transition: `transform ${CLOSE_ANIMATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
        }}
      >
        {/* Modern Drawer Header */}
        <div 
          className="pf-modal-header-wrapper"
          style={{ 
            padding: "16px 22px", 
            background: "#FFFFFF", 
            borderBottom: "1px solid #EEF2F6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0
          }}
        >
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "#0B132B", fontSize: "17px" }}>
              {lang?.create_course || "Create Course"}
            </h5>
            <p className="mb-0 text-muted" style={{ fontSize: "12px" }}>
              {lang?.fill_course_para || "Fill in the details to publish your course"}
            </p>
          </div>
          <button
            type="button"
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            onClick={requestClose}
            aria-label="Close"
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
              cursor: "pointer",
              color: "#64748B",
              transition: "all 0.2s ease",
            }}
          >
            <IoClose size={20} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div style={{ overflowY: "auto", padding: "20px 22px", flex: 1 }}>
          <div className="pf-scope-publish pf-form-body">
            {/* Top AI Highlight Banner */}
            <div className="pf-ai-banner">
              <div className="pf-ai-banner-left">
                <MdOutlineSchool className="pf-ai-banner-sparkle" size={17} />
                <span>Publish Medical Education Course</span>
              </div>
              <span className="pf-ai-banner-tag">Accredited</span>
            </div>

            <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
              {/* Cover Image Upload Card */}
              <div className="pf-card-section">
                <div className="pf-label mb-1">
                  <span className="pf-label-text">
                    <RiImageAddLine className="pf-label-icon" />
                    {lang.add_course_cover || "Course Cover Image"}
                  </span>
                  <span className="text-danger fs-12">*</span>
                </div>

                {croppedImage ? (
                  <div
                    className="position-relative rounded-3 overflow-hidden border"
                    style={{
                      cursor: "pointer",
                      height: "170px",
                      background: "#0F172A",
                    }}
                    onClick={() => toggleImagePreview(croppedImage)}
                  >
                    <img
                      src={croppedImage}
                      alt="Cropped Preview"
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                    <div
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                      style={{ background: "rgba(0,0,0,0.3)", opacity: 0, transition: "opacity 0.2s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
                    >
                      <span className="badge bg-dark bg-opacity-75 text-white px-3 py-1.5 rounded-pill fs-12">
                        Click to preview
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-light position-absolute top-0 end-0 m-2 rounded-circle p-1 d-flex align-items-center justify-content-center shadow-sm"
                      style={{ width: "28px", height: "28px", zIndex: 5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage();
                      }}
                      title="Remove image"
                    >
                      <IoClose size={16} />
                    </button>
                  </div>
                ) : (
                  <div
                    className="pf-upload-zone"
                    onClick={() => {
                      document.getElementById("fileInput").click();
                    }}
                  >
                    <div className="pf-upload-zone-left">
                      <div className="pf-upload-icon-circle">
                        <RiImageAddLine size={22} />
                      </div>
                      <div className="pf-upload-zone-text">
                        <div className="pf-upload-zone-title">
                          {lang.add_course_cover || "Upload Course Cover"}
                        </div>
                        <div className="pf-upload-zone-sub">
                          PNG, JPG or WebP image (Recommended 16:9 or 4:3)
                        </div>
                      </div>
                    </div>
                    <div className="pf-upload-actions">
                      <span
                        className="btn btn-sm rounded-pill fw-semibold"
                        style={{
                          background: "#FFFFFF",
                          border: "1px solid var(--pf-accent-border)",
                          color: "var(--pf-accent-dark)",
                          fontSize: "12px",
                          padding: "6px 14px",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
                        }}
                      >
                        Browse File
                      </span>
                    </div>
                  </div>
                )}
                <input
                  type="file"
                  id="fileInput"
                  className="d-none"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                {errors.cover && (
                  <span className="text-danger mt-1 fs-12">{errors.cover}</span>
                )}
              </div>

              {/* Basic Details: Title, Category & Language */}
              <div className="pf-card-section d-flex flex-column gap-3">
                {/* Course Title */}
                <div className="pf-field">
                  <div className="pf-label">
                    <span className="pf-label-text">
                      {lang.course_title || "Course Title"}
                    </span>
                    <span className="text-muted" style={{ fontSize: "11px" }}>
                      {data.title.length}/50
                    </span>
                  </div>
                  <input
                    type="text"
                    maxLength={50}
                    className="pf-input"
                    placeholder={lang.course_title || "e.g. Masterclass in Ocular Surgery"}
                    value={data.title}
                    onChange={(e) => setData({ ...data, title: e.target.value })}
                  />
                  {errors.title && (
                    <span className="text-danger mt-1 fs-12">{errors.title}</span>
                  )}
                </div>

                {/* Subcategory */}
                <div className="pf-field">
                  <div className="pf-label">
                    <span className="pf-label-text">
                      {lang.select_course_category || "Medical Subspecialty"}
                    </span>
                    <span className="text-danger fs-12">*</span>
                  </div>
                  <select
                    className="pf-select"
                    value={data.subcategory}
                    onChange={(e) => setData({ ...data, subcategory: e.target.value })}
                    style={{ color: data.subcategory === "" ? "#94A3B8" : "#0B132B" }}
                  >
                    <option value="" disabled hidden>
                      {lang.select_course_category || "Select medical field"}
                    </option>
                    {splashData?.home?.subcategories?.map((value, index) => (
                      <option value={value.id} key={index} style={{ color: "#0B132B" }}>
                        {value.name}
                      </option>
                    ))}
                  </select>
                  {errors.subcategory && (
                    <span className="text-danger mt-1 fs-12">{errors.subcategory}</span>
                  )}
                </div>

                {/* Multi-Language */}
                <div className="pf-field">
                  <div className="pf-label">
                    <span className="pf-label-text">
                      {lang.select_language || "Language(s)"}
                    </span>
                    <span className="text-danger fs-12">*</span>
                  </div>
                  <Select
                    isMulti
                    options={languageOptions}
                    value={languageOptions.filter((option) =>
                      data.language.includes(option.value)
                    )}
                    onChange={handleLanguageChange}
                    placeholder={lang.select_language || "Select Language(s)"}
                    styles={customSelectStyles}
                    classNamePrefix="react-select"
                  />
                  {errors.language && (
                    <span className="text-danger mt-1 fs-12">{errors.language}</span>
                  )}
                </div>
              </div>

              {/* Pricing & Free Course Toggle Card */}
              <div className="pf-card-section">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2.5">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "34px", height: "34px", background: "#E0F7FC", color: "#00BADA" }}
                    >
                      <MdAttachMoney size={19} />
                    </div>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: "13px", color: "#0B132B" }}>
                        {lang.course_is_free || "Free Course"}
                      </div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        Allow doctors to enroll for free
                      </div>
                    </div>
                  </div>
                  <label className="cr-switch-toggle mb-0" style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      id="free"
                      checked={data.isFree}
                      onChange={(e) =>
                        setData({
                          ...data,
                          isFree: e.target.checked,
                          price: e.target.checked ? "" : data.price,
                        })
                      }
                    />
                    <span className="cr-switch-slider"></span>
                  </label>
                </div>

                {!data.isFree && (
                  <div className="pf-field mt-3 pt-2 border-top" style={{ borderColor: "#F1F5F9" }}>
                    <div className="pf-label">
                      <span className="pf-label-text">
                        {lang.course_price || "Course Price (€)"}
                      </span>
                      <span className="text-danger fs-12">*</span>
                    </div>
                    <div className="position-relative d-flex align-items-center">
                      <span
                        className="position-absolute ps-3 text-muted fw-bold"
                        style={{ fontSize: "14px", pointerEvents: "none" }}
                      >
                        €
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="pf-input"
                        style={{ paddingLeft: "28px" }}
                        placeholder="0.00"
                        value={data.price}
                        onChange={(e) => setData({ ...data, price: e.target.value })}
                      />
                    </div>
                    {errors.price && (
                      <span className="text-danger mt-1 fs-12">{errors.price}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Course Description */}
              <div className="pf-card-section">
                <div className="pf-field">
                  <div className="pf-label">
                    <span className="pf-label-text">
                      <MdOutlineDescription className="pf-label-icon" />
                      {lang.course_description || "Course Overview / Description"}
                    </span>
                    <span className="text-danger fs-12">*</span>
                  </div>
                  <textarea
                    className="pf-textarea"
                    rows="4"
                    placeholder="Provide a detailed overview of the curriculum and topics covered..."
                    value={data.description}
                    onChange={(e) => setData({ ...data, description: e.target.value })}
                  />
                  {errors.description && (
                    <span className="text-danger mt-1 fs-12">{errors.description}</span>
                  )}
                </div>
              </div>

              {/* Skills Gained (Optional) */}
              <div className="pf-card-section">
                <div className="pf-field">
                  <div className="pf-label">
                    <span className="pf-label-text">
                      <MdLightbulbOutline className="pf-label-icon" />
                      {lang.What_skill_you_will_gain || "What skills will students gain?"}
                    </span>
                    <span className="pf-optional-badge">{lang.opti || "(Optional)"}</span>
                  </div>
                  <textarea
                    className="pf-textarea"
                    rows="3"
                    placeholder="e.g. Clinical diagnosis, Phacoemulsification, Patient management..."
                    value={data.skills}
                    onChange={(e) => setData({ ...data, skills: e.target.value })}
                  />
                </div>
              </div>

              {/* About this course - Key Learning Outcomes (Optional) */}
              <div className="pf-card-section d-flex flex-column gap-2.5">
                <div className="pf-label">
                  <span className="pf-label-text">
                    <MdFormatListBulleted className="pf-label-icon" />
                    {lang.About_this_course || "Key Learning Outcomes"}
                  </span>
                  <span className="pf-optional-badge">{lang.opti || "(Optional)"}</span>
                </div>

                <div className="pf-field">
                  <input
                    type="text"
                    className="pf-input"
                    placeholder={lang.list_key_learning_outcomes_of_course || "Outcome 1: Key learning outcome of course"}
                    value={data.about_1}
                    onChange={(e) => setData({ ...data, about_1: e.target.value })}
                  />
                </div>
                <div className="pf-field">
                  <input
                    type="text"
                    className="pf-input"
                    placeholder={lang.write_important_concept_taught_in_course || "Outcome 2: Important concept taught in course"}
                    value={data.about_2}
                    onChange={(e) => setData({ ...data, about_2: e.target.value })}
                  />
                </div>
                <div className="pf-field">
                  <input
                    type="text"
                    className="pf-input"
                    placeholder={lang.what_learning_objective_or_focus_here || "Outcome 3: Learning objective or core focus"}
                    value={data.about_3}
                    onChange={(e) => setData({ ...data, about_3: e.target.value })}
                  />
                </div>
                <div className="pf-field">
                  <input
                    type="text"
                    className="pf-input"
                    placeholder={lang.highlight_additional_takeaway_from_course || "Outcome 4: Additional takeaway from course"}
                    value={data.about_4}
                    onChange={(e) => setData({ ...data, about_4: e.target.value })}
                  />
                </div>
              </div>

              {/* Accredited Certificates (Optional) */}
              <div className="pf-card-section">
                {certificates.length === 0 ? (
                  <div
                    className="pf-upload-zone"
                    onClick={() => setIsCertificateModelOpen(true)}
                  >
                    <div className="pf-upload-zone-left">
                      <div className="pf-upload-icon-circle">
                        <MdWorkspacePremium size={20} />
                      </div>
                      <div className="pf-upload-zone-text">
                        <div className="pf-upload-zone-title">
                          {lang.add_certificates || "Accredited Certificates"}
                          <span className="pf-optional-badge ms-2">{lang.opti || "(Optional)"}</span>
                        </div>
                        <div className="pf-upload-zone-sub">
                          Attach accredited certificate issued upon completion
                        </div>
                      </div>
                    </div>
                    <div className="pf-upload-actions">
                      <button type="button" className="pf-chip-btn">
                        <span>+ {lang.add_certificates || "Add Certificate"}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="pf-label">
                        <span className="pf-label-text">
                          <MdWorkspacePremium className="pf-label-icon" />
                          {lang.add_certificates || "Accredited Certificates"}
                        </span>
                        <span className="pf-optional-badge">{lang.opti || "(Optional)"}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsCertificateModelOpen(true)}
                        className="pf-chip-btn"
                      >
                        <MdWorkspacePremium size={14} />
                        <span>Add Another</span>
                        <span className="pf-chip-count">{certificates.length}</span>
                      </button>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      {certificates.map((cert, index) => (
                        <div
                          key={index}
                          className="pf-file-chip cursor-pointer"
                          style={{
                            borderColor: "var(--pf-accent-border)",
                            background: "var(--pf-accent-light)",
                          }}
                          onClick={() => handleOpenModalCertificateModal(cert, index)}
                        >
                          <MdWorkspacePremium size={15} color="var(--pf-accent-dark)" />
                          <span
                            className="pf-file-name"
                            style={{ color: "var(--pf-accent-dark)" }}
                          >
                            {cert.title || `${lang.certificate || "Certificate"} ${index + 1}`}
                          </span>
                          <button
                            type="button"
                            className="pf-file-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveCertificate(index);
                            }}
                            title="Remove certificate"
                          >
                            <RiCloseLine size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Bottom Action Footer matching post form tabs */}
              <div className="pf-footer-row mt-3" style={{ justifyContent: "flex-end" }}>
                <div className="pf-footer-actions">
                  <button
                    type="button"
                    className="pf-btn-cancel"
                    onClick={requestClose}
                  >
                    {lang.cancel || "Cancel"}
                  </button>
                  <button
                    type="submit"
                    className="pf-btn-submit"
                    disabled={
                      !croppedImage ||
                      !data.title ||
                      !data.subcategory ||
                      !data.description ||
                      (!data.isFree && !data.price) ||
                      data.language.length === 0 ||
                      isLoading
                    }
                  >
                    {isLoading ? (
                      <span className="d-flex align-items-center gap-2">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span>Adding Course...</span>
                      </span>
                    ) : (
                      <span>{lang.add_course || "Publish Course"}</span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ImageCropModal
        isOpen={isCropModalOpen}
        toggle={() => setIsCropModalOpen(!isCropModalOpen)}
        imageSrc={imageSrc}
        aspect={0.86}
        onCropComplete={handleCropComplete}
      />
      <Modal
        isOpen={isImagePreviewOpen !== null}
        toggle={() => setIsImagePreviewOpen(null)}
        centered
        className="image-preview-modal"
      >
        <img src={isImagePreviewOpen} alt="Full Preview" className="img-fluid" />
      </Modal>
      {isCertificateModelOpen && (
        <CertificateModal
          isOpen={isCertificateModelOpen}
          toggle={handleCloseModalCertificateModal}
          onSave={handleAddCertificate}
          ImagePreview={toggleImagePreview}
          initialCertificate={selectedCertificate}
        />
      )}
    </>
  );
};

export default CreateCourseModal;